import {useEffect, useMemo, useCallback, useRef} from 'react';
import {REWARD_TYPES} from '@/utils/constants/rewardsConstants';
import {
  isSingleImageRewardType,
  renderPreviewContent,
  getInitialTitle,
  shouldDisableEnAutofill
} from '@/utils/rewardsDisplay';
import {useRewardsDisplayLanguages} from './useRewardsDisplayLanguages';
import {useRewardsDisplayProducts} from './useRewardsDisplayProducts';
import {useRewardsDisplayImages} from './useRewardsDisplayImages';

/**
 * Main hook for rewards display - composes smaller focused hooks
 * to reduce cognitive complexity
 */
export const useRewardsDisplay = ({
  dispatch,
  formData,
  handleFormChange,
  countryCode,
  displayContentLanguages,
  currentStep
}) => {
  const {languages} = useRewardsDisplayLanguages({
    countryCode,
    displayContentLanguages
  });

  const rewardType = formData?.rewardType || '';
  const displaySingleImageUpload = useMemo(
    () => isSingleImageRewardType(formData?.rewardType),
    [formData?.rewardType]
  );

  const rewardDescription = useMemo(() => {
    const preview = renderPreviewContent({formData, countryCode});
    return preview || formData?.name || '';
  }, [formData, countryCode]);

  const enFallbackDescription = useMemo(() => {
    return renderPreviewContent({formData, countryCode}) || '';
  }, [formData, countryCode]);

  const enSyncPrimaryProductId = useMemo(() => {
    if (formData?.rewardType === REWARD_TYPES.PEP_VOUCHER_PRODUCT) {
      return formData?.selectedPepVouchersProducts?.[0]?.value ?? '';
    }
    if (formData?.rewardType === REWARD_TYPES.PRODUCT) {
      return formData?.selectedProducts?.[0]?.value ?? '';
    }
    return '';
  }, [
    formData?.rewardType,
    formData?.selectedProducts,
    formData?.selectedPepVouchersProducts
  ]);

  const {
    display,
    effectiveProducts,
    productOptions,
    productChildList,
    completedCount,
    activeProductId,
    setActiveProductId,
    updateProductDisplay,
    updateProductDisplayFor,
    onProductChange,
    updateRewardText,
    showToast,
    setShowToast,
    toastMessage,
    showToastNotification,
    fetchAndSetDefaultImages
  } = useRewardsDisplayProducts({
    dispatch,
    formData,
    handleFormChange,
    languages,
    currentStep,
    enFallbackDescription
  });

  const {
    useDefaultImageToggleValue,
    handleUseDefaultImageToggleChange,
    isImageUploading,
    alertDisplayCount,
    setAlertDisplayCount,
    showOnHomePage,
    handleShowOnHomePageChange,
    imagesForActiveProduct,
    onImageSelectAt,
    imageUpload
  } = useRewardsDisplayImages({
    display,
    activeProductId,
    updateProductDisplay,
    showToastNotification,
    fetchAndSetDefaultImages,
    currentStep,
    formData,
    handleFormChange
  });

  const updateGlobalRewardText = useCallback(
    (language, patch) => {
      const currentText = formData?.rewardText || {};
      const disableAutofill = shouldDisableEnAutofill(language, patch);
      const nextRewardText = {
        ...currentText,
        [language]: {...currentText[language], ...patch},
        ...(disableAutofill ? {enAutofillDisabled: true} : {})
      };
      handleFormChange('rewardText', nextRewardText);
    },
    [formData?.rewardText, handleFormChange]
  );

  // Seed global rewardText with null for all language codes not yet present
  useEffect(() => {
    if (currentStep !== 2 || languages.length === 0) return;
    const isBonusProducts =
      formData?.rewardType === REWARD_TYPES.BONUS_PRODUCTS;
    if (isBonusProducts) return;

    const currentText = formData?.rewardText || {};
    let changed = false;
    const seeded = {...currentText};
    languages.forEach((lang) => {
      if (!(lang.code in seeded)) {
        seeded[lang.code] = null;
        changed = true;
      }
    });
    if (changed) {
      handleFormChange('rewardText', seeded);
    }
  }, [
    currentStep,
    languages,
    formData?.rewardType,
    formData?.rewardText,
    handleFormChange
  ]);

  const lastEnAutofillProductIdRef = useRef(undefined);
  const loadedPrimaryProductIdRef = useRef(null);
  const baselineRewardIdRef = useRef(undefined);

  // Single effect: rewardId baseline reset → clear sync when not PRODUCT/PEP → seed edit baseline product id
  useEffect(() => {
    const rid = formData?.rewardId || '';
    const rt = formData?.rewardType;
    const isPep = rt === REWARD_TYPES.PEP_VOUCHER_PRODUCT;
    const isProd = rt === REWARD_TYPES.PRODUCT;

    if (rid !== baselineRewardIdRef.current) {
      baselineRewardIdRef.current = rid;
      lastEnAutofillProductIdRef.current = undefined;
      loadedPrimaryProductIdRef.current = null;
    }

    if (!isPep && !isProd) {
      lastEnAutofillProductIdRef.current = undefined;
    }

    if (
      rid &&
      loadedPrimaryProductIdRef.current == null &&
      enSyncPrimaryProductId &&
      (isPep || isProd)
    ) {
      loadedPrimaryProductIdRef.current = enSyncPrimaryProductId;
    }
  }, [formData?.rewardId, formData?.rewardType, enSyncPrimaryProductId]);

  const setGlobalEnFromProduct = useCallback(() => {
    const title = getInitialTitle(formData, displaySingleImageUpload);
    if (!title) return false;
    const currentText = formData?.rewardText || {};
    const next = {
      ...currentText,
      en: {title, description: rewardDescription}
    };
    delete next.enAutofillDisabled;
    handleFormChange('rewardText', next);
    return true;
  }, [formData, displaySingleImageUpload, rewardDescription, handleFormChange]);

  useEffect(() => {
    if (currentStep !== 2 || !rewardDescription) return;

    const currentEn = formData?.rewardText?.en || {};
    const hasTitle =
      typeof currentEn.title === 'string' && currentEn.title.trim().length > 0;
    const hasDesc =
      typeof currentEn.description === 'string' &&
      currentEn.description.trim().length > 0;

    const isAutofillDisabled = formData?.rewardText?.enAutofillDisabled;

    const isProductOrPep =
      formData?.rewardType === REWARD_TYPES.PRODUCT ||
      formData?.rewardType === REWARD_TYPES.PEP_VOUCHER_PRODUCT;

    const updateEn = (title) => {
      if (!title) return;
      updateGlobalRewardText('en', {
        title: hasTitle ? currentEn.title : title,
        description: hasDesc ? currentEn.description : rewardDescription
      });
    };

    const handleSameProductFallback = () => {
      if (isAutofillDisabled || (hasTitle && hasDesc)) return;
      const title = getInitialTitle(formData, displaySingleImageUpload);
      updateEn(title);
    };

    if (isProductOrPep && enSyncPrimaryProductId) {
      const primary = enSyncPrimaryProductId;
      const last = lastEnAutofillProductIdRef.current;
      const loaded = loadedPrimaryProductIdRef.current;

      if (last === undefined) {
        if (
          formData?.rewardId &&
          loaded &&
          primary === loaded &&
          hasTitle &&
          hasDesc
        ) {
          lastEnAutofillProductIdRef.current = primary;
          return;
        }
        if (setGlobalEnFromProduct()) {
          lastEnAutofillProductIdRef.current = primary;
        }
        return;
      }

      if (last !== primary) {
        if (setGlobalEnFromProduct()) {
          lastEnAutofillProductIdRef.current = primary;
        }
        return;
      }

      handleSameProductFallback();
      return;
    }

    if (isAutofillDisabled || (hasTitle && hasDesc)) return;
    const title = getInitialTitle(formData, displaySingleImageUpload);
    updateEn(title);
  }, [
    currentStep,
    rewardDescription,
    displaySingleImageUpload,
    enSyncPrimaryProductId,
    formData,
    updateGlobalRewardText,
    setGlobalEnFromProduct
  ]);

  return {
    imageUpload,
    updateGlobalRewardText,
    rewardType,
    displaySingleImageUpload,
    languages,
    rewardDescription,
    display,
    effectiveProducts,
    productOptions,
    productChildList,
    completedCount,
    activeProductId,
    setActiveProductId,
    updateProductDisplay,
    updateProductDisplayFor,
    useDefaultImageToggleValue,
    handleUseDefaultImageToggleChange,
    isImageUploading,
    alertDisplayCount,
    setAlertDisplayCount,
    showOnHomePage,
    handleShowOnHomePageChange,
    imagesForActiveProduct,
    onImageSelectAt,
    onProductChange,
    updateRewardText,
    showToast,
    setShowToast,
    toastMessage
  };
};
